'use client'

import * as React from 'react'
import Image from 'next/image'
import type { FeatureRowBlock as FeatureRowBlockProps } from '@/payload-types'
import * as Lucide from 'lucide-react'
import { Quote } from 'lucide-react'
import { motion, AnimatePresence, Variants } from 'framer-motion'
import { CMSLink } from '@/components/Link'
import { bgClass } from '@/utilities/bgClass'

const Icons = Lucide as unknown as Record<string, React.ComponentType<any>>

const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: 'easeOut' } },
}

/* ---------- helpers ---------- */
function getImageMeta(row: any) {
  const media = row?.media as any
  return {
    url: media?.url ?? media?.sizes?.large?.url ?? media?.sizes?.card?.url,
    alt: media?.alt ?? row?.heading ?? 'Feature image',
    width: media?.width ?? media?.sizes?.large?.width ?? 1600,
    height: media?.height ?? media?.sizes?.large?.height ?? 900,
  }
}

function MediaBoxCover({ url, alt }: { url?: string; alt: string }) {
  return (
    <div className="relative w-full h-60 md:h-90 rounded-2xl overflow-hidden shadow">
      {url ? (
        <Image src={url} alt={alt} fill className="object-cover" sizes="(min-width:1024px) 50vw, 100vw" />
      ) : (
        <div className="h-full w-full border border-dashed border-neutral-300 dark:border-neutral-700 bg-neutral-50/60 dark:bg-neutral-900/40" />
      )}
    </div>
  )
}

function MediaBoxEmbed({
  src,
  title,
}: {
  src: string
  title: string
}) {
  return (
    <div className="relative w-full h-125 md:h-180 rounded-2xl overflow-hidden shadow">
      <iframe
        src={src}
        title={title}
        className="h-full w-full border-0"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
    </div>
  )
}

function MediaBoxNatural({
  url,
  alt,
  width,
  height,
}: {
  url?: string
  alt: string
  width: number
  height: number
}) {
  return url ? (
    <Image
      src={url}
      alt={alt}
      width={width}
      height={height}
      className="w-full h-auto rounded-2xl"   // no shadow
      sizes="100vw"
    />
  ) : (
    <div className="h-56 md:h-72 w-full rounded-2xl" />  // no border/shadow
  )
}


export const FeatureRowBlock: React.FC<FeatureRowBlockProps> = ({ title, rows }) => {
  return (
    <section>
      {title ? (
        <div className="container px-4 lg:px-6 xl:px-8 pt-10 md:py-14 text-center">
          <h2 className="text-3xl md:text-4xl xl:text-[2.9rem] xl:leading-14 text-mini-primary dark:text-mini-dark-primary mb-6 font-medium">{title}</h2>
        </div>
      ) : null}

      {(rows ?? []).map((row, idx) => {
        const position = (row.mediaPosition ?? 'right') as 'left' | 'right' | 'above' | 'below'
        const mediaMode = (row as any)?.mediaMode ?? 'image' // 'image' | 'embed' | 'none'
        const embedUrl = typeof (row as any)?.embedUrl === 'string' ? (row as any).embedUrl.trim() : ''
        const imageLeft = position === 'left'
        const Icon = row.iconName && Icons[row.iconName] ? Icons[row.iconName] : undefined

        const { url: imageUrl, alt: imageAlt, width: imgW, height: imgH } = getImageMeta(row)

        const isHeaderRow = mediaMode === 'none' || position === 'above' || position === 'below'
        const gridCols = imageLeft ? 'lg:grid-cols-[1.6fr_1.4fr]' : 'lg:grid-cols-[1.4fr_1.6fr]'

        const quotes = Array.isArray(row?.testimonials)
          ? (row.testimonials as Array<{ quote?: string; attribution?: string }>).filter(
              (q) => typeof q?.quote === 'string' && q.quote.trim().length > 0,
            )
          : []
        const hasCarousel = quotes.length > 1
        const [active, setActive] = React.useState(0)
        const hoverRef = React.useRef(false)
        const lastUserNavRef = React.useRef(0)
        const rowTitle = (row as any)?.rowTitle || undefined

        React.useEffect(() => {
          if (!hasCarousel) return
          const id = setInterval(() => {
            const now = Date.now()
            const userPause = now - lastUserNavRef.current < 10000
            if (hoverRef.current || userPause) return
            setActive((i) => (i + 1) % quotes.length)
          }, 10000)
          return () => clearInterval(id)
        }, [hasCarousel, quotes.length])

        const paginate = (dir: number) => {
          lastUserNavRef.current = Date.now()
          setActive((i) => (i + dir + quotes.length) % quotes.length)
        }

        const rowLinks: any[] = Array.isArray(row.links) ? (row.links as any[]) : []

        const HeaderArea = (
          <>
            {row.badge || Icon ? (
              <div className="flex items-center gap-3 mb-5">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-green-100 dark:bg-minigreen-6">
                  {Icon ? <Icon className="h-5 w-5 text-minigreen-6 dark:text-minigreen-1" strokeWidth={3} /> : null}
                </span>
                {row.badge ? (
                  <span className="text-2xl font-medium font-sans text-minigreen-6 dark:text-minigreen-1">
                    {row.badge}
                  </span>
                ) : null}
              </div>
            ) : null}

            {row.heading ? (
              <h1 className="mb-6">
                {row.heading}
              </h1>
            ) : null}

            {row.description ? (
              <p className="text-lg leading-relaxed">
                {row.description}
              </p>
            ) : null}
          </>
        )

        const Quotes = quotes.length > 0 && (
          <div
            className="mt-6"
            onMouseEnter={() => (hoverRef.current = true)}
            onMouseLeave={() => (hoverRef.current = false)}
          >
            <div className="relative rounded-3xl rounded-l-xl bg-minigreen-bg-light dark:bg-minigreen-7 shadow-sm p-8 pb-4 overflow-hidden">
              <div className="absolute left-0 top-0 h-full w-2.5 rounded-l-3xl bg-minigreen-4 dark:bg-minigreen-5" />
              <div className="absolute left-5 top-2 rotate-180 text-minigreen-4 dark:text-minigreen-5">
                <Quote className="w-8 h-8 fill-minigreen-4 dark:fill-minigreen-5" strokeWidth={1} />
              </div>

              <div className="relative min-h-25">
                <AnimatePresence initial={false} mode="wait">
                  <motion.div
                    key={active}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={0.6}
                    onDragEnd={(e, info) => {
                      if (info.offset.x < -80) paginate(1)
                      else if (info.offset.x > 80) paginate(-1)
                    }}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.5 }}
                    className="absolute inset-0"
                  >
                    <blockquote className="relative pl-8 text-neutral-800 dark:text-neutral-200 italic -mt-3 cursor-grab active:cursor-grabbing">
                      {quotes[active].quote}
                      {quotes[active].attribution ? (
                        <p className="mt-3 pl-3 text-neutral-700 dark:text-neutral-300 not-italic">— {quotes[active].attribution}</p>
                      ) : null}
                    </blockquote>
                  </motion.div>
                </AnimatePresence>
              </div>

              {hasCarousel && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  {quotes.map((_, i) => (
                    <button
                      key={i}
                      aria-label={`Go to slide ${i + 1}`}
                      onClick={() => {
                        lastUserNavRef.current = Date.now()
                        setActive(i)
                      }}
                      className={`h-2.5 rounded-full transition-all ${
                        i === active
                          ? 'bg-minigreen-5 dark:bg-minigreen-2 w-6'
                          : 'bg-minigreen-3 hover:bg-minigreen-5 dark:bg-minigreen-4 dark:hover:bg-minigreen-1 w-2.5'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        )

        return (
          <div key={idx} className={`${bgClass(row.background)} w-full`}>
            <motion.div
              className="container px-4 lg:px-6 xl:px-8 py-12 md:py-28"
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.25 }}
            >
              {rowTitle ? (
                <h1 className="text-center mb-20">
                  {rowTitle}
                </h1>
              ) : null}

              {/* Header-style rows (Above/Below OR None) */}
              {isHeaderRow ? (
                <div className="space-y-8 md:space-y-10">
                  {/* Image Above (if mode is image) */}
                  {position === 'above' && mediaMode !== 'none' ? (
                    mediaMode === 'embed' && embedUrl ? (
                      <MediaBoxEmbed src={embedUrl} title={rowTitle || row.heading || 'Embedded media'} />
                    ) : (
                      <MediaBoxNatural url={imageUrl} alt={imageAlt} width={imgW} height={imgH} />
                    )
                  ) : null}

                  {/* Header left, CTAs right */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div className="max-w-3xl">{HeaderArea}</div>
                    {rowLinks.length > 0 ? (
                      <ul className="shrink-0 flex flex-wrap gap-3 md:self-center">
                        {rowLinks.map((item: any, i: number) => {
                          const link = item?.link ?? item
                          if (!link) return null
                          const key = item?.id ?? link?.id ?? link?.url ?? link?.href ?? i
                          return (
                            <li key={key}>
                              <CMSLink {...link} />
                            </li>
                          )
                        })}
                      </ul>
                    ) : null}
                  </div>


                  {Quotes}

                  {/* Image Below (if mode is image) */}
                  {position === 'below' && mediaMode !== 'none' ? (
                    mediaMode === 'embed' && embedUrl ? (
                      <MediaBoxEmbed src={embedUrl} title={rowTitle || row.heading || 'Embedded media'} />
                    ) : (
                      <MediaBoxNatural url={imageUrl} alt={imageAlt} width={imgW} height={imgH} />
                    )
                  ) : null}
                </div>
              ) : (
                // Classic left/right row with image and CTAs under the copy
                <div className={`grid grid-cols-1 md:grid-cols-2 ${gridCols} gap-10 lg:gap-18 items-center`}>
                  {/* Image */}
                  <div className={`order-2 ${imageLeft ? 'md:order-1' : 'md:order-2'}`}>
                    {mediaMode === 'embed' && embedUrl ? (
                      <MediaBoxEmbed src={embedUrl} title={rowTitle || row.heading || 'Embedded media'} />
                    ) : (
                      <MediaBoxCover url={imageUrl} alt={imageAlt} />
                    )}
                  </div>

                  {/* Text */}
                  <div className={`order-1 ${imageLeft ? 'md:order-2' : 'md:order-1'}`}>
                    {HeaderArea}
                    {Quotes}

                    {rowLinks.length > 0 && (
                      <ul className="mt-8 flex flex-wrap gap-4">
                        {rowLinks.map((item: any, i: number) => {
                          const link = item?.link ?? item
                          if (!link) return null
                          const key = item?.id ?? link?.id ?? link?.url ?? link?.href ?? i
                          return (
                            <li key={key}>
                              <CMSLink {...link} />
                            </li>
                          )
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )
      })}
    </section>
  )
}

export default FeatureRowBlock